/**
 * IndexedDB Initialization Test
 *
 * This file can be used to test and verify IndexedDB is working correctly.
 * Import and call initIndexedDB() in your app initialization to create the database.
 */

import indexedDB, { STORES } from '@/utils/indexedDB';

/**
 * Initialize IndexedDB database and verify it's working
 * This will create the database and all object stores
 */
export async function initIndexedDB(): Promise<boolean> {
  try {
    // Check if IndexedDB is available
    const isAvailable = await indexedDB.isAvailable();

    if (!isAvailable) {
      return false;
    }

    // Test write operation
    await indexedDB.set(
      STORES.CARGO_LIST,
      'test-key',
      { test: 'data', timestamp: Date.now() },
      { ttl: 5 * 60 * 1000 } // 5 minutes TTL
    );

    // Test read operation
    const testData = await indexedDB.get(STORES.CARGO_LIST, 'test-key');

    if (!testData) {
      return false;
    }

    // Get database stats
    await indexedDB.getStats(STORES.CARGO_LIST);

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Test all IndexedDB operations
 */
export async function testIndexedDB(): Promise<void> {
  try {
    // Test 1: Write to multiple stores
    await Promise.all([
      indexedDB.set(STORES.CARGO_LIST, 'test-cargo', { id: 1, name: 'Test Cargo' }),
      indexedDB.set(STORES.CARGO_COUNT, 'test-count', { count: 42 }),
      indexedDB.set(STORES.BALTIC, 'test-route', { route: 'Test Route' }),
    ]);

    // Test 2: Read operations
    await indexedDB.get(STORES.CARGO_LIST, 'test-cargo');
    await indexedDB.get(STORES.CARGO_COUNT, 'test-count');

    // Test 3: Get all keys
    await indexedDB.getAllKeys(STORES.CARGO_LIST);

    // Test 4: TTL expiration
    await indexedDB.set(
      STORES.CARGO_LIST,
      'test-ttl',
      { test: 'expires soon' },
      { ttl: 100 } // 100ms
    );

    // Wait for expiration
    await new Promise((resolve) => setTimeout(resolve, 150));
    await indexedDB.get(STORES.CARGO_LIST, 'test-ttl');

    // Test 5: Clear expired entries
    await indexedDB.clearExpired(STORES.CARGO_LIST);

    // Test 6: Get statistics
    await indexedDB.getStats(STORES.CARGO_LIST);
  } catch (error) {
    // Test failed
  }
}

/**
 * Clear all test data
 */
export async function clearTestData(): Promise<void> {
  try {
    await indexedDB.delete(STORES.CARGO_LIST, 'test-key');
    await indexedDB.delete(STORES.CARGO_LIST, 'test-cargo');
    await indexedDB.delete(STORES.CARGO_COUNT, 'test-count');
    await indexedDB.delete(STORES.BALTIC, 'test-route');
  } catch (error) {
    // Failed to clear test data
  }
}

// Pre-initialize database on module load
// This ensures IndexedDB is ready before stores try to use it
const dbInitPromise = initIndexedDB();

// Export the initialization promise so stores can wait for it if needed
export { dbInitPromise };

// Auto-run in development mode
if (import.meta.env.DEV) {
  dbInitPromise.then(() => {
    // Auto-initialization complete
  });
}
